import { Component, computed, input } from '@angular/core';
import { AdesaoStatus } from '../../../core/enum/adesao-status.enum';
import { CampanhaStatus } from '../../../core/enum/campanha-status.enum';
import { IntegracaoParticipanteStatus } from '../../../core/enum/integracao-participante-status.enum';
import { QuestionarioStatus } from '../../../core/enum/questionario-status.enum';
import { normalizarTexto } from '../../../core/utils/texto.util';

const STATUS_MODIFIER: Record<string, string> = {
  [CampanhaStatus.Rascunho]: 'status-badge--rascunho',
  [CampanhaStatus.Agendada]: 'status-badge--pendente',
  [CampanhaStatus.AtivoPortal]: 'status-badge--ativa',
  [CampanhaStatus.Encerrada]: 'status-badge--fechada',
  [CampanhaStatus.Cancelada]: 'status-badge--falha',
  [CampanhaStatus.Pausada]: 'status-badge--pendente',
  // Status de adesão: amarelo (pendente), primário (solicitado), verde
  // (concluído) e vermelho (cancelado).
  [AdesaoStatus.Pendente]: 'status-badge--pendente',
  [AdesaoStatus.Solicitado]: 'status-badge--solicitado',
  [AdesaoStatus.Concluido]: 'status-badge--concluido',
  [AdesaoStatus.Cancelada]: 'status-badge--cancelado',
  [QuestionarioStatus.Publicado]: 'status-badge--publicado',
  [QuestionarioStatus.Rascunho]: 'status-badge--rascunho',
  [IntegracaoParticipanteStatus.Aguardando]: 'status-badge--pendente',
  [IntegracaoParticipanteStatus.Sucesso]: 'status-badge--concluido',
  [IntegracaoParticipanteStatus.Erro]: 'status-badge--falha',
  // `descLog` do log do participante vem cru do back e usa "Falha" onde a lista usa "Erro".
  'Falha': 'status-badge--falha',
  'Ativo': 'status-badge--ativa',
  'Inativo': 'status-badge--inativo',
};

/**
 * Andamento da integração da campanha (tela de Integração). O back manda essa informação como
 * descrição (`descricaoStatus`) e não como código — todas essas campanhas são Encerradas —,
 * então a chave é o texto normalizado, imune a acento e caixa.
 *
 * Os tons são os mesmos da Gestão de Campanhas, reaproveitando os modificadores que já os
 * carregam: amarelo de Agendada (`--pendente`), roxo de Encerrada (`--solicitado`, o mesmo
 * primary-v50/v800 que a listagem aplica via `--status-badge-fechada-*`), vermelho de Cancelada
 * (`--falha`) e verde de Ativa (`--concluido`).
 */
const STATUS_INTEGRACAO_MODIFIER: Record<string, string> = {
  'aguardando integracao': 'status-badge--pendente',
  'integracao iniciada': 'status-badge--solicitado',
  'integracao parcial': 'status-badge--falha',
  'integracao concluida': 'status-badge--concluido',
};

@Component({
  selector: 'app-status-badge',
  imports: [],
  templateUrl: './status-badge.component.html',
  styleUrl: './status-badge.component.scss',
})
export class StatusBadgeComponent {
  readonly status = input.required<string>();
  /** Texto exibido no badge — default para `status()` quando o próprio código já é legível. */
  readonly label = input<string>();

  readonly displayLabel = computed(() => this.label() ?? this.status());

  readonly badgeClasses = computed(() => {
    const status = this.status();
    const modifier =
      STATUS_MODIFIER[status] ??
      STATUS_INTEGRACAO_MODIFIER[normalizarTexto(status)] ??
      'status-badge--fechada';

    return `status-badge ${modifier}`;
  });
}
